import Autosuggest from "react-autosuggest";
import React, { useCallback, useEffect, useMemo, useState } from "react";

type LangSuggestion = { name: string };

type ReactAutosuggestProps = {
  data?: LangSuggestion[];
  placeholder?: string;
  onChange?: (value: string) => void;
};

function ReactAutosuggest({
  data: dataProp = [],
  placeholder = "",
  onChange
}: ReactAutosuggestProps) {
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState<LangSuggestion[]>([]);
  const [data, setData] = useState<LangSuggestion[]>(dataProp);

  useEffect(() => {
    setData(dataProp);
  }, [dataProp]);

  const getSuggestionValue = useCallback(
    (suggestion: LangSuggestion) => suggestion.name,
    []
  );

  const renderSuggestion = useCallback(
    (suggestion: LangSuggestion) => <div>{suggestion.name}</div>,
    []
  );

  const getSuggestions = useCallback(
    (input: string) => {
      const inputValue = input.trim().toLowerCase();
      const inputLength = inputValue.length;
      if (inputLength === 0) {
        return [];
      }
      return data.filter(
        lang =>
          lang.name.toLowerCase().slice(0, inputLength) === inputValue
      );
    },
    [data]
  );

  const handleChange = useCallback(
    (_event: unknown, { newValue }: { newValue: string }) => {
      setValue(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value: v }: { value: string }) => {
      setSuggestions(getSuggestions(v));
    },
    [getSuggestions]
  );

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
  }, []);

  const inputProps = useMemo(
    () => ({
      placeholder,
      value,
      onChange: handleChange
    }),
    [handleChange, placeholder, value]
  );

  const theme = useMemo(
    () => ({
      container: "autosuggest",
      input: "form-control",
      inputOpen: "react-autosuggest__input--open",
      suggestionsContainer: "react-autosuggest__suggestions-container",
      suggestionsContainerOpen:
        "react-autosuggest__suggestions-container--open",
      suggestionsList: `react-autosuggest__suggestions-list ${
        suggestions.length ? "show" : ""
      }`,
      suggestionFocused: "active",
      suggestion: "react-autosuggest__suggestion"
    }),
    [suggestions.length]
  );

  return (
    <Autosuggest<LangSuggestion>
      suggestions={suggestions}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion}
      inputProps={inputProps}
      theme={theme}
    />
  );
}

export default ReactAutosuggest;
